import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AppThunk, RootState } from "store"
import WorkMonitorShiftsService from "services/workMonitor/workMonitor.service"
import { convertIOSDateTZToSpecificFormat, updateState } from "config/utils"
import { DATE_FORMATS, ShiftStatus, platformTypeENUM } from "config/constants"
import {
	ShiftInterface,
	UpdateWorkMonitor,
	ResolveIncidentDto,
	ShiftFiltersInterface,
	IncidentReportInterface,
	PopulatedShiftInterface,
	UpdateMultipleWorkMonitor,
} from "@type/workMonitor.types"
import WorkMonitorShiftsManager from "store/workMonitor/workMonitorManager"

export interface CommandCenterStateInterface {
	list: PopulatedShiftInterface[]
	shiftFilters: ShiftFiltersInterface | null
	loading: boolean
	saving: boolean
	nextLoading: boolean
}

const initialState: CommandCenterStateInterface = {
	list: [],
	shiftFilters: null,
	loading: false,
	saving: false,
	nextLoading: false,
}

const processShiftsForCommandCenter = (list: ShiftInterface[]): PopulatedShiftInterface[] => {
	const result = list
		.filter(item => item && !item.shiftEvents.some(({ status }) => status == ShiftStatus.BOOKOFF))
		.map(shift => {
			const { finishTime, startTime, shiftEvents = [] } = shift
			const { action, nextStatusTime } = WorkMonitorShiftsManager.getNextShiftEvent(shift)

			const status = WorkMonitorShiftsManager.getShiftsStatusFromNextEvent(shift)

			const { isInBefore, isInAfter, isLate } = WorkMonitorShiftsManager.deriveShiftUIState(
				shiftEvents,
				action,
				nextStatusTime,
			)

			return {
				...shift,
				startTime: convertIOSDateTZToSpecificFormat(startTime, DATE_FORMATS.SHIFT_EXPECTED_TIME_FORMAT),
				finishTime: convertIOSDateTZToSpecificFormat(finishTime, DATE_FORMATS.SHIFT_EXPECTED_TIME_FORMAT),
				action,
				status,
				isInBefore,
				isInAfter,
				isLate,
				nextStatusTime,
			}
		})

	return result
}

const commandCenterSlice = createSlice({
	name: "commandCenter",
	initialState,
	reducers: {
		fetchingActiveShifts: state => {
			state.loading = true
		},
		activeShiftsFetched: (state, action: PayloadAction<ShiftInterface[]>) => {
			state.loading = false
			state.list = processShiftsForCommandCenter(action.payload)
		},
		addActiveShift: (state, action: PayloadAction<ShiftInterface>) => {
			state.saving = false
			const [updatedState] = updateState(state.list, action.payload, "_id")

			state.list = processShiftsForCommandCenter(updatedState)
		},
		updateActiveShiftList: (state, action: PayloadAction<ShiftInterface>) => {
			state.saving = false
			const [updatedState] = updateState(state.list, action.payload, "_id")
			const filterActiveShifts = updatedState.filter(shift => !!shift.isActive)

			state.list = processShiftsForCommandCenter(filterActiveShifts)
		},
		activeShiftFetchingFailed: state => {
			state.loading = false
		},
		savingShift: state => {
			state.saving = true
		},
		savingShiftFailed: state => {
			state.saving = false
		},
		updateShiftColors: state => {
			state.list = [...processShiftsForCommandCenter(state.list)]
		},
		deleteShift: (state, action: PayloadAction<string>) => {
			const filterData = state.list.filter(shift => shift._id !== action.payload)
			state.list = filterData
		},

		clearCommandCenter: state => {
			state.list = []
			state.loading = false
		},
		saveShiftFilters: (state, action: PayloadAction<ShiftFiltersInterface | null>) => {
			state.shiftFilters = action.payload
		},
		clearShiftFilters: state => {
			state.shiftFilters = null
		},
	},
})

//REDUCER
export default commandCenterSlice.reducer

//ACTIONS
export const {
	fetchingActiveShifts,
	activeShiftsFetched,
	activeShiftFetchingFailed,
	addActiveShift,
	clearCommandCenter,
	savingShift,
	savingShiftFailed,
	clearShiftFilters,
	deleteShift,
	saveShiftFilters,
	updateActiveShiftList,
	updateShiftColors,
} = commandCenterSlice.actions

const getActiveShifts = (): AppThunk => async dispatch => {
	// let shiftIdsToExclude: string[] = getState().workMonitor.processedList.map(shift => shift._id)

	try {
		dispatch(fetchingActiveShifts())
		const { data: activeShiftsResponse } = await WorkMonitorShiftsService.getActiveShifts()
		dispatch(activeShiftsFetched(activeShiftsResponse))
	} catch (error) {
		dispatch(activeShiftFetchingFailed())
	}
}

const updateShifts =
	(shiftId: string, data: UpdateWorkMonitor, cb?: () => void): AppThunk =>
	async dispatch => {
		try {
			dispatch(savingShift())
			const { data: activeShiftResponse } = await WorkMonitorShiftsService.updateShifts(shiftId, {
				...data,
				source: platformTypeENUM.WEB,
			})
			dispatch(updateActiveShiftList(activeShiftResponse))
			cb?.()
		} catch (error) {
			dispatch(savingShiftFailed())
		}
	}

const updateMultipleShifts =
	(data: UpdateMultipleWorkMonitor[], cb?: () => void): AppThunk =>
	async dispatch => {
		try {
			dispatch(savingShift())
			const { data: activeShiftsResponse } = await WorkMonitorShiftsService.updateMultipleShifts(data)
			dispatch(updateActiveShiftList(activeShiftsResponse))
			cb?.()
		} catch (error) {
			dispatch(savingShiftFailed())
		}
	}

const addIncidentInShift =
	(shiftId: string, data: IncidentReportInterface, cb?: () => void): AppThunk =>
	async (dispatch, getState) => {
		try {
			dispatch(savingShift())
			const { data: shiftIncidentResponse } = await WorkMonitorShiftsService.addIncidentInShift(shiftId, data)

			const activeShiftList = getState().commandCenter.list

			if (activeShiftList.length && shiftIncidentResponse) {
				const updatedActiveShiftList = [...activeShiftList]

				const shiftToUpdate = updatedActiveShiftList.find(({ _id }) => _id === shiftIncidentResponse.shift)

				if (shiftToUpdate) {
					const updatedShift = {
						...shiftToUpdate,
						incidents: [...shiftToUpdate.incidents, { ...shiftIncidentResponse }],
					}

					dispatch(updateActiveShiftList(updatedShift))
				}
			}
			dispatch(savingShiftFailed())
			cb?.()
		} catch (error) {
			dispatch(savingShiftFailed())
		}
	}

const resolveIncidentInShift =
	(incidentId: string, data: ResolveIncidentDto, cb?: () => void): AppThunk =>
	async (dispatch, getState) => {
		try {
			dispatch(savingShift())
			const { data: resolvedShiftIncidentResponse } = await WorkMonitorShiftsService.resolveIncidentInShift(
				incidentId,
				data,
			)

			const activeShiftList = getState().commandCenter.list

			if (activeShiftList.length && resolvedShiftIncidentResponse) {
				const updatedActiveShiftList = [...activeShiftList]

				const shiftToUpdate = updatedActiveShiftList.find(
					({ _id }) => _id === resolvedShiftIncidentResponse.shift,
				)

				if (shiftToUpdate) {
					const existingIncidents = [...shiftToUpdate.incidents]

					const [updatedIncidents] = updateState(existingIncidents, resolvedShiftIncidentResponse, "_id")

					const updatedShift = {
						...shiftToUpdate,
						incidents: updatedIncidents,
					}

					dispatch(updateActiveShiftList(updatedShift))
				}
			}
			dispatch(savingShiftFailed())

			cb?.()
		} catch (error) {
			dispatch(savingShiftFailed())
		}
	}

export { getActiveShifts, updateShifts, addIncidentInShift, updateMultipleShifts, resolveIncidentInShift }

//SELECTORS
const selectCommandCenterState = (state: RootState) => state.commandCenter
const selectCommandCenterLoading = () => (state: RootState) => selectCommandCenterState(state).loading
const selectCommandCenterNextLoading = () => (state: RootState) => selectCommandCenterState(state).nextLoading
const selectCommandCenterSaving = () => (state: RootState) => selectCommandCenterState(state).saving
const selectShiftFilter = () => (state: RootState) => selectCommandCenterState(state).shiftFilters

const selectWorkMonitorDataStructureChanged = createSelector(
	(state: RootState) => ({
		processedList: state.commandCenter.list,
		shiftFilters: state.commandCenter.shiftFilters,
	}),
	({ processedList, shiftFilters }) => {
		if (shiftFilters) {
			const filteredList = processedList.filter(list => {
				const {
					employeeBranch = null,
					supplier = null,
					locationBranch = null,
					customer = null,
					upcomingShift = false,
					onGoingShift = false,
					startingSoon = false,
					endingSoon = false,
				} = shiftFilters

				const isDropdownFilterActive = ["employeeBranch", "supplier", "locationBranch", "customer"].some(
					key => !!shiftFilters[key as keyof ShiftFiltersInterface],
				)
				let dropdownFilter = !isDropdownFilterActive

				if (isDropdownFilterActive) {
					dropdownFilter = !!(
						employeeBranch?.includes(list?.employeeBranch || "") ||
						supplier?.includes(list?.employeeSupplier || "") ||
						locationBranch?.includes(list?.locationBranch || "") ||
						customer?.includes(list?.customerId || "")
					)
				}

				const isBothLocationAndCustomerFilter =
					locationBranch && customer
						? locationBranch?.includes(list?.locationBranch || "") &&
						  customer?.includes(list?.customerId || "")
						: true

				const isToggleFilterActive = ["upcomingShift", "onGoingShift", "startingSoon", "endingSoon"].some(
					key => !!shiftFilters[key as keyof ShiftFiltersInterface],
				)

				let toggleFilter = !isToggleFilterActive

				if (isToggleFilterActive) {
					toggleFilter =
						(upcomingShift && list.action === ShiftStatus.CHASED) ||
						(onGoingShift && list.status === ShiftStatus.BOOKON) ||
						(startingSoon && list.action === ShiftStatus.BOOKON) ||
						(endingSoon && list.action === ShiftStatus.BOOKOFF)
				}

				return dropdownFilter && toggleFilter && isBothLocationAndCustomerFilter
			})

			return WorkMonitorShiftsManager.sortShiftsByDateAndStatus(filteredList)
		} else return WorkMonitorShiftsManager.sortShiftsByDateAndStatus(processedList)
	},
)

export {
	selectCommandCenterSaving,
	selectCommandCenterLoading,
	selectWorkMonitorDataStructureChanged,
	selectCommandCenterNextLoading,
	selectShiftFilter,
}
